<script setup lang="ts">
import { computed } from 'vue'

import type { TixDate } from '@tix/tix-time'

import vFocus from '../../../directives/focus'

import { formatDayPrice, getAvailabilityLabel } from './helpers'
import type { DayData } from './types'

interface Props {
  dayOfMonth: number
  day: DayData
  displayPrices: boolean
  capacityThreshold: number | false
  focusedDate?: TixDate
}

const props = defineProps<Props>()

const emit = defineEmits<{
  select: [day: DayData]
  focus: [day: DayData]
}>()

const enabled = computed(() => props.day.enabled)

const shouldDisplayPrices = computed(() => {
  return props.displayPrices && props.day.price !== undefined
})

const isFocused = computed(() => {
  return props.focusedDate && props.focusedDate.isSame(props.day.date)
})

const isAvailabilityDisplayed = computed(() => {
  return props.capacityThreshold && props.day.available !== undefined && props.day.available < props.capacityThreshold
})

const formattedPrice = computed(() => formatDayPrice(props.day))
const availabilityLabel = computed(() => getAvailabilityLabel(props.day, props.capacityThreshold))

/**
 * Class binding for availability status severity.
 * Placeholder: always danger when below threshold.
 * Future: vary severity (e.g. warning vs danger) based on how far below capacityThreshold availability is.
 * See web-client AvailabilityStatus / ecomm-prev.
 */
const availabilitySeverityClass = computed(() => ({ danger: true }))

function onClick() {
  if (enabled.value) {
    emit('select', props.day)
  }
}

function onFocus() {
  if (enabled.value) {
    emit('focus', props.day)
  }
}
</script>

<template>
  <button
    type="button"
    v-focus="isFocused"
    :disabled="!enabled"
    :aria-label="day.ariaLabel"
    class="picker-day"
    :class="[day.className, { focused: isFocused, disabled: !enabled }]"
    :tabindex="isFocused ? 0 : -1"
    :title="day.tooltip"
    @click.prevent="onClick"
    @focus="onFocus"
  >
    <span class="date">{{ dayOfMonth }}</span>

    <template v-if="enabled">
      <span v-if="shouldDisplayPrices" class="price">{{ formattedPrice }}</span>

      <span v-if="isAvailabilityDisplayed" class="availability-status" :class="availabilitySeverityClass">
        {{ availabilityLabel }}
      </span>
      <!-- Placeholder for availability so that dots align consistently. -->
      <span v-else-if="capacityThreshold !== false" class="availability-status">&nbsp;</span>

      <span v-if="day.dots.length > 0" class="dots">
        <span v-for="(dot, index) of day.dots" :key="index" class="dot" :class="dot.style">{{ dot.symbol }}</span>
      </span>
    </template>
  </button>
</template>

<style scoped>
.picker-day {
  --_day-text-color: var(--text-primary);
  --_day-bg: transparent;
  --_day-bg-hover: var(--elevation-0);
  /* TODO: Replace with semantic token (e.g. --neutral-disabled-surface-color) when added to design-system tokens. Light = gray-100, dark = gray-800. */
  --_day-bg-disabled: var(--gray-50);
  --_day-text-disabled: var(--text-secondary);
  --_price-text-color: var(--text-secondary);
  --_availability-text-color: var(--text-secondary);
  --_focus-outline-color: var(--text-link);

  background: var(--_day-bg);
  border: none;
  border-spacing: 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-width: 66px;
  min-height: 100%;
  color: var(--_day-text-color);

  @media (--mobile-experience) {
    min-width: 0;

    > * {
      min-width: 0;
    }
  }
  text-align: center;
  position: relative;
  font-family: var(--font-family-body);
  cursor: pointer;
  padding: 10px 4px;
  gap: 3px;

  &:hover,
  &:focus {
    background: var(--_day-bg-hover);
  }

  &.focused {
    outline: 2px solid var(--_focus-outline-color);
    outline-offset: -2px;

    &:hover,
    &:focus {
      background: color-mix(in srgb, var(--_focus-outline-color) 4%, transparent);
    }
  }

  .date {
    margin: 0;
    font-weight: 600;
    font-size: 14px;
    line-height: 16px;
    color: var(--_day-text-color);
  }

  .price {
    font-size: 11px;
    line-height: 12px;
    margin: 0;
    padding: 0;
    color: var(--_price-text-color);
    width: 100%;
  }

  .availability-status {
    font-size: 10px;
    line-height: 11px;
    color: var(--_availability-text-color);

    &.danger {
      color: var(--error-text-color);
    }
  }

  .dots {
    display: flex;
    gap: 3px;
    justify-content: center;
    margin-top: auto;
    height: 6px;
    line-height: 0;

    .dot {
      margin: 0;
      padding: 0;
      line-height: 0;
      width: 6px;
      height: 6px;
      border-radius: 50%;

      &.info {
        background: var(--info-text-color);
      }

      &.warning {
        background: var(--warning-text-color);
      }

      &.error {
        background: var(--error-text-color);
      }

      &.success {
        background: var(--success-text-color);
      }

      &.increase {
        background: var(--warning-text-color);
      }
    }
  }

  &.disabled {
    cursor: not-allowed;
    background: var(--_day-bg-disabled);
    color: var(--_day-text-disabled);

    &:hover,
    &:focus {
      background: var(--_day-bg-disabled);
    }

    .date {
      color: var(--_day-text-disabled);
    }

    &.sold-out .date {
      text-decoration: line-through;
    }
  }
}

:root.dark-theme .picker-day.disabled {
  --_day-bg-disabled: var(--gray-800);
}
</style>
